/**
 * ui/SeedIO — import / export de la configuration en « seed » (JSON).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * UI — candidat `@mostajs/app-admin-ui-react`. N'invente RIEN côté serveur :
 *  • Export = `GET /api/config` → **arbre `Config.data` brut** = format **`agency-*.json`**
 *    (identique aux datasets `server/seed/data/`, consommables par `seed-demo.js` pour
 *    fabriquer une `.db` d'un nouveau site) ;
 *  • Import = `PUT /api/config` (gardé, permission) → nouvelle version + reload ;
 *    tolérant : lit un fichier brut `agency-*.json` OU l'ancien emballage `{config}`.
 * Un seed = instantané portable de la config (transférer/restaurer/amorcer un site, D10).
 * Backup COMPLET (config + binaires + users) = différé → réutiliser MostaGare / futur `@mostajs/backup`.
 */
import React, { useRef, useState } from 'react';

export default function SeedIO({ api, reload }) {
  const fileRef = useRef(null);
  const [msg, setMsg] = useState('');

  const exportSeed = async () => {
    setMsg('');
    try {
      // Export = l'arbre `Config.data` BRUT → drop-in `server/seed/data/agency-*.json`
      // (même format que les datasets consommés par seed-demo.js). Pas d'emballage.
      const config = await api.getConfig();
      const stamp = new Date().toISOString().slice(0, 10);
      // Nom dérivé de la config (générique) ; si `agency.name` existe (cas TicketFlow),
      // le fichier est un drop-in `agency-*.json`. Aucune dépendance à une app précise.
      const slug = String(config?.agency?.name || config?.name || 'config')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'config';
      const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agency-${slug}-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(String(e?.message || e));
    }
  };

  const importSeed = async (file) => {
    if (!file) return;
    // Garde-fou : l'import écrase la configuration active.
    if (!window.confirm('Importer ce seed remplacera la configuration active. Continuer ?')) {
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setMsg('…');
    try {
      const parsed = JSON.parse(await file.text());
      const config = parsed && parsed.config ? parsed.config : parsed; // tolérant
      await api.saveConfig(config);
      await reload?.();
      setMsg('✓ importé');
    } catch (e) {
      setMsg(e?.response?.status === 403 ? 'Accès refusé (rôle)' : `Import invalide : ${e?.message || e}`);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <span className="seed-io">
      <button className="ghost" onClick={exportSeed} title="Exporter la configuration (seed)">⬇ seed</button>
      <button className="ghost" onClick={() => fileRef.current?.click()} title="Importer un seed">⬆ seed</button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => importSeed(e.target.files?.[0])}
      />
      {msg && <span className="status">{msg}</span>}
    </span>
  );
}
